'use client'

import { useCallback } from 'react'
import { useSWRConfig } from 'swr'

import { useApiQuery } from '@/hooks/use-api'
import { useOptionalCurrentBrand } from '@/hooks/use-current-brand'

function unreadCountUrlFor(brandSlug: string | null): string {
  return brandSlug
    ? `/api/notifications/unread-count?brand=${encodeURIComponent(brandSlug)}`
    : '/api/notifications/unread-count'
}

/**
 * Sidebar bell badge feed. Polls ``GET /api/notifications/unread-count``
 * every 15s, which SWR pauses automatically when the tab is hidden
 * (``refreshWhenHidden: false`` is the SWR default). Tab focus triggers
 * an immediate revalidate so a tab swap surfaces missed notifications
 * within one round trip instead of waiting for the next interval.
 *
 * Brand scoped: the badge counts unread for the active brand workspace
 * only (plus org-level rows with no brand). Outside a brand layout the
 * scope falls back to the legacy unscoped count.
 *
 * ``refresh`` is exposed for the popover to call on open so the badge
 * count matches the freshly-opened list without waiting for the next
 * tick.
 */
export function useNotificationUnreadCount(): {
  unreadCount: number
  refresh: () => Promise<void>
  isLoading: boolean
} {
  const { mutate } = useSWRConfig()
  const brand = useOptionalCurrentBrand()
  const url = unreadCountUrlFor(brand?.brandSlug ?? null)
  const { data, isLoading } = useApiQuery<{ count: number }>(url, {
    refreshInterval: 15_000,
    revalidateOnFocus: true,
    // Last-known count is a better stale UX than flashing 0 between revalidations.
    keepPreviousData: true,
  })

  const refresh = useCallback(async () => {
    await mutate(url)
  }, [mutate, url])

  return {
    unreadCount: data?.count ?? 0,
    refresh,
    isLoading,
  }
}

/**
 * Cross-brand ambient feed for the brand switcher. Polls
 * ``GET /api/notifications/unread-count-by-brand`` every 15s (same cadence
 * as the bell badge; SWR pauses it when the tab is hidden) and returns a
 * ``{ brandId: count }`` map for every brand in the active org that has
 * unread, brand-scoped notifications.
 *
 * Distinct from ``useNotificationUnreadCount``: that drives the red bell
 * badge for the *current* brand; this drives the neutral dots on *other*
 * brands so a finished task surfaces without re-polluting the current
 * brand's bell. The endpoint is org-required — outside an org it 404s and
 * SWR surfaces empty counts (no dots), which is the desired UX.
 */
export function useUnreadCountByBrand(): {
  countsByBrand: Record<string, number>
  isLoading: boolean
} {
  const { data, isLoading } = useApiQuery<{ counts: Record<string, number> }>(
    '/api/notifications/unread-count-by-brand',
    {
      refreshInterval: 15_000,
      revalidateOnFocus: true,
      keepPreviousData: true,
    },
  )

  return {
    countsByBrand: data?.counts ?? {},
    isLoading,
  }
}
